import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { Authenticator } from '@okta/okta-auth-js';
import { IdxForm } from '@okta/okta-auth-js/types/lib/idx/types/idx-js';
import { InitUser, SetUserSignInFlag } from 'src/app/core/store/user/user.actions';
import { asFormControl } from 'src/app/core/utils/common';

@Component({
  selector: 'app-verify-code-form',
  templateUrl: './verify-code-form.component.html',
  styleUrls: ['./verify-code-form.component.scss'],
})
export class VerifyCodeFormComponent implements OnInit {
  @Input() onUsePassword!: () => void;
  @Input() factor!: 'email' | 'phone';
  @Input() email!: string;
  @Input() phone!: string;

  verifyCodeForm = new FormGroup({
    code1: new FormControl(null, [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(1),
      Validators.pattern(/^\d+$/),
    ]),
    code2: new FormControl(null, [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(1),
      Validators.pattern(/^\d+$/),
    ]),
    code3: new FormControl(null, [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(1),
      Validators.pattern(/^\d+$/),
    ]),
    code4: new FormControl(null, [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(1),
      Validators.pattern(/^\d+$/),
    ]),
    code5: new FormControl(null, [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(1),
      Validators.pattern(/^\d+$/),
    ]),
    code6: new FormControl(null, [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(1),
      Validators.pattern(/^\d+$/),
    ]),
  });
  disableButton: boolean = false;
  sendingCode: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  formError: boolean = false;

  asFormControl = asFormControl;

  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth, private store: Store) {}

  ngOnInit(): void {
    this.verifyCodeForm.valueChanges.subscribe((values) => {
      this.formError = false;

      if (values.code1 && values.code1.length === 6) {
        this.verifyCodeForm.get('code1')?.setValue(values.code1[0]);
        this.verifyCodeForm.get('code2')?.setValue(values.code1[1]);
        this.verifyCodeForm.get('code3')?.setValue(values.code1[2]);
        this.verifyCodeForm.get('code4')?.setValue(values.code1[3]);
        this.verifyCodeForm.get('code5')?.setValue(values.code1[4]);
        this.verifyCodeForm.get('code6')?.setValue(values.code1[5]);
      } else {
        Object.keys(values).forEach((key) => {
          if (values[key]?.length > 1) {
            this.verifyCodeForm.get(key)?.setValue(values[key].slice(-1));
          }
        });

        if (values.code6) {
          return;
        }
        if (values.code5) {
          const element = document.getElementById('code6');

          if (element) {
            element.focus();
          }

          return;
        }
        if (values.code4) {
          const element = document.getElementById('code5');

          if (element) {
            element.focus();
          }

          return;
        }
        if (values.code3) {
          const element = document.getElementById('code4');

          if (element) {
            element.focus();
          }

          return;
        }
        if (values.code2) {
          const element = document.getElementById('code3');

          if (element) {
            element.focus();
          }

          return;
        }
        if (values.code1) {
          const element = document.getElementById('code2');

          if (element) {
            element.focus();
          }

          return;
        }
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (this.verifyCodeForm.invalid) {
      this.verifyCodeForm.markAllAsTouched();
      this.formError = true;
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';
    this.disableButton = true;
    this.formError = false;

    const code = Object.values(this.verifyCodeForm.value).join('');

    try {
      const authenticateResponse = await this.oktaAuth.idx.authenticate({
        credentials: {
          passcode: code,
        },
      });

      if (authenticateResponse.status === 'SUCCESS') {
        const { tokens } = authenticateResponse;

        if (!tokens) {
          throw new Error('There was a problem validating the code. Please try again.');
        }

        this.store.dispatch(new SetUserSignInFlag(true)).subscribe(() => {
          setTimeout(async () => {
            await this.oktaAuth.tokenManager.setTokens(tokens);

            this.store.dispatch(new InitUser());
          }, 0);
        });
      } else {
        this.errorMessage = 'You have entered an invalid code. Please try again.';
        this.disableButton = false;
      }
    } catch (error: any) {
      this.errorMessage = error.message;
      this.disableButton = false;
    }
  }

  async resendCode(): Promise<void> {
    if (this.sendingCode) {
      return;
    }

    this.sendingCode = true;
    this.successMessage = '';
    this.errorMessage = '';

    try {
      await this.oktaAuth.idx.clearTransactionMeta();

      const factorResponse = await this.oktaAuth.idx.authenticate({
        username: this.email,
      });
      const selectAuthenticator = factorResponse.neededToProceed.find(
        (item) => item.name === 'select-authenticator-authenticate'
      );
      const authenticators = selectAuthenticator?.value?.find((item) => item.name === 'authenticator');
      const factor = authenticators?.options?.find((item) => item.label.toLowerCase() === this.factor.toLowerCase());

      if (!factor) {
        throw new Error('This option is not available on your account.');
      }

      const id = (factor.value as { form: IdxForm }).form.value.find((item) => item.name === 'id')?.value as string;

      const payload: Authenticator & {
        enrollmentId?: string;
      } = {
        id,
      };

      if (this.factor === 'phone') {
        payload.enrollmentId = (factor.value as { form: IdxForm }).form.value.find(
          (item) => item.name === 'enrollmentId'
        )?.value as string;
      }

      // Set type of authentication
      await this.oktaAuth.idx.authenticate({
        authenticator: payload,
      });

      if (this.factor === 'email') {
        payload.methodType = 'email';
      } else if (this.factor === 'phone') {
        payload.methodType = 'sms';
      }

      // Send code
      const sendResponse = await this.oktaAuth.idx.authenticate({
        authenticator: payload,
      });

      if (sendResponse.status !== 'PENDING') {
        if (sendResponse.messages?.[0].i18n.key === 'errors.E0000150') {
          throw new Error(sendResponse.messages?.[0].message);
        }

        throw new Error('Something went wrong. Please try again.');
      }

      this.successMessage = `A new code has been sent to your ${this.factor}.`;
      this.sendingCode = false;

      this.verifyCodeForm.reset();
    } catch (error: any) {
      this.errorMessage = error.message;
      this.sendingCode = false;
    }
  }
}
